import React, { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { useSelector, useDispatch } from 'react-redux';
import { getPerformanceData } from '../redux/reducer';
import i18next from '../translations/i18n';
import PeriodSelector from './PeriodSelector';
import Loader from './Loader';

const PerformanceGrowthChart = () => {
  const [chartData, setChartData] = useState({
    fundName: '',
    benchmarkName: '',
    dates: [],
    fundSeries: [],
    bmkSeries: [],
    prefData: [],
  });
  const { timePeriods, selectedPeriodFilteredData } = useSelector(state => state.app);

  const dispatch = useDispatch();
  useEffect(() => {
    const fetchData = async () => {
      let fundSymbol = window.performanceChart?.tickerCode ?? '';

      // Ensure string, normalize NBSPs, then trim both ends
      if (typeof fundSymbol !== 'string') {
        fundSymbol = String(fundSymbol || '');
      }
      fundSymbol = fundSymbol.replace(/\u00A0/g, ' ').trim();

      // If tickerCode contains a slash, keep only the first segment
      if (fundSymbol.includes('/')) {
        fundSymbol = fundSymbol.split('/')[0].trim();
      }

      if (fundSymbol.length === 0) {
        // eslint-disable-next-line no-console
        console.warn('PerformanceGrowthChart: empty fundSymbol after trim');
      }

      await dispatch(getPerformanceData({ entity: fundSymbol, mode: 'gro10k' }));
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedPeriodFilteredData && selectedPeriodFilteredData.length > 0) {
      const fundName = selectedPeriodFilteredData.find(d => d.wtTypeID === 1)?.name || '';
      const benchmarkName = selectedPeriodFilteredData.find(d => d.wtTypeID !== 1)?.name || '';
      const prefData = [];

      // Step 1: Build chartData with rebasing using officialLevel
      // Find base levels for fund and benchmark from the first available date
      const datesSet = new Set(selectedPeriodFilteredData.map(d => d.cobDate));
      const dates = Array.from(datesSet).sort((a, b) => new Date(a) - new Date(b));
      const firstDate = dates[0];

      const baseFundLevel = selectedPeriodFilteredData.find(
        d => d.cobDate === firstDate && d.wtTypeID === 1,
      )?.officialLevel;
      const baseBmkLevel = selectedPeriodFilteredData.find(
        d => d.cobDate === firstDate && d.wtTypeID !== 1,
      )?.officialLevel;

      dates.forEach(date => {
        const fundData = selectedPeriodFilteredData.find(
          d => d.cobDate === date && d.wtTypeID === 1,
        );
        const bmkData = selectedPeriodFilteredData.find(
          d => d.cobDate === date && d.wtTypeID !== 1,
        );

        const rebasedFund =
          fundData && baseFundLevel ? (fundData.officialLevel / baseFundLevel) * 10000 : null;
        const rebasedBmk =
          bmkData && baseBmkLevel ? (bmkData.officialLevel / baseBmkLevel) * 10000 : null;

        const cumFund = rebasedFund !== null ? rebasedFund / 10000 - 1 : null;
        const cumBmk = rebasedBmk !== null ? rebasedBmk / 10000 - 1 : null;

        prefData.push({
          date,
          rebasedFund,
          rebasedBmk,
          cumFund,
          cumBmk,
        });
      });

      setChartData({
        fundName,
        benchmarkName,
        prefData,
      });
    }
  }, [selectedPeriodFilteredData]);

  const formatDate = dtStr => {
    const dt = new Date(dtStr);

    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    };
    return dt.toLocaleDateString('en-US', options);
  };

  const normalizeSeries = data =>
    data.map(([date, level]) => {
      const pct = (level / 10000 - 1) * 100;
      return [date, parseFloat(pct.toFixed(1))];
    });

  const option = {
    grid: { top: 30, right: 8, bottom: 85, left: 21, containLabel: true },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        label: {
          formatter: params => {
            if (params.axisDimension === 'y') {
              return `${params.value.toFixed(1)} %`;
            }
            return `${formatDate(params.value)}`;
          },
        },
      },
      valueFormatter: value => `${value.toFixed(1)} %`,
      textStyle: {
        color(value) {
          if (value < 0) {
            return '#dc3545';
          }
          return '#000';
        },
      },
    },
    legend: { data: [chartData.fundName, chartData.benchmarkName], bottom: 0 },
    xAxis: {
      type: 'time',
      onZero: false,
      nameLocation: 'middle',
    },
    yAxis: {
      type: 'value',
      name: i18next.t('PERCENTAGE'),
      nameLocation: 'middle',
      nameGap: 55,
      nameTextStyleObject: {
        fontSize: 20,
      },
      axisLabel: {
        formatter: value => `${value.toFixed(1)} %`,
        textStyle: {
          color(value) {
            if (value < 0) {
              return '#dc3545';
            }
            return '#000';
          },
        },
      },
    },
    series: [
      ...(chartData.benchmarkName
        ? [
            {
              name: chartData.benchmarkName,
              type: 'line',
              data: normalizeSeries(chartData.prefData.map(d => [d.date, d.rebasedBmk])),
              showSymbol: false,
              color: '#03a2ab',
            },
          ]
        : []),
      ...(chartData.fundName
        ? [
            {
              name: chartData.fundName,
              type: 'line',
              data: normalizeSeries(chartData.prefData.map(d => [d.date, d.rebasedFund])),
              showSymbol: false,
              color: '#050139',
            },
          ]
        : []),
    ],
  };

  return (
    <div>
      {selectedPeriodFilteredData.length > 0 && (
        <>
          <div className="wt-chart-container">
            <PeriodSelector periods={timePeriods} />
          </div>
          <ReactECharts
            option={option}
            notMerge
            lazyUpdate
            onChartReady={() => {}}
            onEvents={() => {}}
            style={{
              width: '100%',
            }}
            opts={{ renderer: 'svg' }}
          />
        </>
      )}
      {selectedPeriodFilteredData.length === 0 && <Loader />}
    </div>
  );
};

export default PerformanceGrowthChart;
